import { useId, useMemo, useState, type FormEvent } from 'react';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  CalendarDays,
  Check,
  CheckCheck,
  ChevronRight,
  ClipboardCheck,
  FileCode2,
  FileText,
  House,
  Info,
  Loader2,
  MessageSquare,
  RefreshCw,
  TriangleAlert
} from 'lucide-react';
import { LwButton, LwDataPanel, LwModal, LwStatusPill, LwThemeScope, type LwStatusTone } from '@/components/lw';
import type { AddPaymentLinkInput, PaymentLinkReminderInput } from '@/services/appraisalWorkspaceService';
import type {
  AppraisalEvent,
  AppraisalOrder,
  AppraisalPaymentReminderOutcome,
  AppraisalPaymentReminderPreview,
  AppraisalReport,
  AppraisalSnapshot,
  AppraisalUnresolvedPaymentLink
} from '../../../../../supabase/functions/_shared/appraisal-workspace-contract';
import { AppraisalFeeComplianceCard, feeAuditStatus } from './AppraisalFeeComplianceCard';
import { appraisalDate } from './appraisalDate';
import {
  AddPaymentLinkModal,
  AppraisalActionsMenu,
  PaymentLinkPanel,
  ResolveReminderModal,
  SendPaymentReminderDialog
} from './AppraisalPaymentLinks';
import './appraisal.css';

export interface AppraisalWorkspaceProps {
  snapshot: AppraisalSnapshot | null;
  loading?: boolean;
  error?: string | null;
  busy?: string | null;
  initialReportId?: string;
  initialFeeVerificationId?: string;
  onRefresh: () => void;
  onAudit: (reportId: string) => void;
  onUpload: (file: File, reportId?: string) => Promise<void>;
  onResolve: (reportId: string, note: string) => Promise<void>;
  onDownload: (reportId: string, kind: 'pdf' | 'xml') => void;
  onResourceDownload: (orderId: string, resourceId: string, name: string) => void;
  onPreviewReminder?: (requestId: string) => Promise<AppraisalPaymentReminderPreview>;
  onSendReminder?: (input: PaymentLinkReminderInput) => Promise<AppraisalPaymentReminderOutcome>;
  onAddPaymentLink?: (input: AddPaymentLinkInput) => Promise<void>;
  onListUnresolvedPaymentLinks?: () => Promise<AppraisalUnresolvedPaymentLink[]>;
  onResolveReminder?: (input: { attemptId: string; delivered: boolean; note: string }) => Promise<void>;
}

const STATE_LABELS: Record<string, string> = {
  pending: 'Pending',
  not_required: 'Not needed',
  assessment: 'Findings only',
  added: 'Added in Encompass',
  exists: 'Already on loan',
  review: 'Needs review',
  unknown: 'Needs verification',
  failed: 'Failed',
  sending: 'Sending',
  sent: 'Sent'
};
function stateTone(state: string): LwStatusTone {
  if (['added', 'exists', 'sent', 'not_required'].includes(state)) {
    return 'success';
  }
  if (state === 'failed') {
    return 'destructive';
  }
  if (state === 'unknown' || state === 'review') {
    return 'warning';
  }
  return 'neutral';
}
function orderLabel(order: AppraisalOrder | undefined): string {
  if (!order) {
    return 'Uploaded appraisal';
  }
  if (order.kind === 'repair_inspection') {
    return '1004D · Repair inspection';
  }
  if (order.kind === 'recertification') {
    return '1004D · Recertification';
  }
  return order.product_code || 'Appraisal order';
}
function eventTitle(event: AppraisalEvent): string {
  const titles: Record<string, string> = {
    VendorAcceptedAssignment: 'Assignment accepted',
    InspectionScheduled: 'Inspection scheduled',
    Completed: event.kind === 'delivery' ? 'Appraisal delivered' : 'Provider update',
    'Comment-ActionRequired': 'Action requested'
  };
  return titles[event.text] || event.text || 'Order update';
}

function TimelineEntry({
  event,
  order,
  onDownload
}: {
  event: AppraisalEvent;
  order?: AppraisalOrder;
  onDownload: AppraisalWorkspaceProps['onResourceDownload'];
}) {
  const Icon = event.kind === 'message' ? MessageSquare : event.kind === 'delivery' ? FileText : Check;
  return (
    <li className={`appraisal-event appraisal-event--${event.kind}`}>
      <span className="appraisal-event__icon" aria-hidden="true">
        <Icon size={15} />
      </span>
      <div className="appraisal-event__body">
        <div className="appraisal-event__top">
          <h4>{eventTitle(event)}</h4>
          <time dateTime={event.occurred_at}>{appraisalDate(event.occurred_at, true)}</time>
        </div>
        <div className="appraisal-event__byline">
          {event.sender || 'Provider'}
          {order && (
            <>
              {' '}
              <span aria-hidden="true">·</span> {order.kind === 'full' ? 'Original appraisal' : orderLabel(order)}
            </>
          )}
        </div>
        {event.comments &&
          (event.comments.length > 400 ? (
            <details className="appraisal-message">
              <summary>
                Read full message <ChevronRight size={14} aria-hidden="true" />
              </summary>
              <p>{event.comments}</p>
            </details>
          ) : (
            <p className="appraisal-event__message">{event.comments}</p>
          ))}
        {event.resources.length > 0 && (
          <div className="appraisal-event__files">
            {event.resources.map((resource) => (
              <button
                type="button"
                key={resource.id}
                onClick={() => onDownload(event.order_id, resource.id, resource.name)}
              >
                <ArrowDownToLine size={13} aria-hidden="true" />
                <span>{resource.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}

function XmlUploadModal({
  report,
  onClose,
  onUpload
}: {
  report?: AppraisalReport;
  onClose: () => void;
  onUpload: AppraisalWorkspaceProps['onUpload'];
}) {
  const formId = useId();
  const fileId = useId();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!file || busy) {
      return;
    }
    if (!/\.xml$/i.test(file.name) || file.size > 64 * 1024 * 1024 || file.size === 0) {
      setError('Choose a nonempty XML file up to 64 MiB.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onUpload(file, report?.has_xml ? undefined : report?.id);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The XML could not be uploaded.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <LwModal
      className="appraisal-modal"
      eyebrow="APPRAISAL SOURCE"
      title="Add appraisal XML"
      sub="Upload the original appraisal XML to check the property, capture report facts, and run the 1004D audit."
      onClose={onClose}
      footer={
        <>
          <LwButton variant="sm" onClick={onClose}>
            Cancel
          </LwButton>
          <LwButton variant="sm-solid" type="submit" form={formId} disabled={!file || busy}>
            {busy && <Loader2 size={15} className="animate-spin" aria-hidden="true" />}
            {busy ? 'Uploading XML…' : 'Upload & audit'}
          </LwButton>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(event) => {
          void submit(event);
        }}
        className="appraisal-upload-form"
      >
        <label htmlFor={fileId} className="appraisal-file-label">
          <FileCode2 size={27} aria-hidden="true" />
          <strong>Original appraisal XML</strong>
          <span>Choose the XML supplied by the appraiser or AMC.</span>
        </label>
        <input
          id={fileId}
          type="file"
          accept=".xml,application/xml,text/xml"
          disabled={busy}
          onChange={(event) => {
            setFile(event.target.files?.[0] || null);
            setError(null);
          }}
        />
        {file && (
          <p className="appraisal-file-choice">
            {file.name} <span>· {(file.size / 1024 / 1024).toFixed(1)} MiB</span>
          </p>
        )}
        <p className="appraisal-help">
          The property and report identity must match this loan. Existing report versions are preserved.
        </p>
        {error && (
          <p role="alert" className="appraisal-error">
            {error}
          </p>
        )}
      </form>
    </LwModal>
  );
}

function ResolveModal({
  report,
  onClose,
  onResolve
}: {
  report: AppraisalReport;
  onClose: () => void;
  onResolve: AppraisalWorkspaceProps['onResolve'];
}) {
  const id = useId();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <LwModal
      className="appraisal-modal"
      eyebrow="MANUAL HANDLING"
      title="Close the missing XML notice"
      sub="Record why XML is unobtainable and how the appraisal was handled. This does not mark the audit as passed."
      onClose={onClose}
      footer={
        <>
          <LwButton variant="sm" onClick={onClose}>
            Cancel
          </LwButton>
          <LwButton
            variant="sm-solid"
            disabled={busy || note.trim().length < 10}
            onClick={() => {
              setBusy(true);
              setError(null);
              void onResolve(report.id, note.trim())
                .then(onClose)
                .catch((cause: unknown) =>
                  setError(cause instanceof Error ? cause.message : 'The resolution could not be saved.')
                )
                .finally(() => setBusy(false));
            }}
          >
            {busy ? 'Saving…' : 'Mark handled manually'}
          </LwButton>
        </>
      }
    >
      <div className="appraisal-upload-form">
        <label htmlFor={id}>Resolution note</label>
        <textarea id={id} value={note} onChange={(event) => setNote(event.target.value)} rows={4} maxLength={4000} />
        <p className="appraisal-help">Your name and the resolution date will be recorded.</p>
        {error && (
          <p role="alert" className="appraisal-error">
            {error}
          </p>
        )}
      </div>
    </LwModal>
  );
}

export function AppraisalWorkspace({
  snapshot,
  loading = false,
  error,
  busy,
  initialReportId,
  initialFeeVerificationId,
  onRefresh,
  onAudit,
  onUpload,
  onResolve,
  onDownload,
  onResourceDownload,
  onPreviewReminder,
  onSendReminder,
  onAddPaymentLink,
  onListUnresolvedPaymentLinks,
  onResolveReminder
}: AppraisalWorkspaceProps) {
  const [selectedId, setSelectedId] = useState(initialReportId || '');
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [filter, setFilter] = useState<'all' | 'message' | 'update'>('all');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  // Each payment dialog remembers the Actions trigger so focus returns to it.
  const [sendFrom, setSendFrom] = useState<HTMLElement | null | false>(false);
  const [addFrom, setAddFrom] = useState<HTMLElement | null | false>(false);
  const [resolveAttempt, setResolveAttempt] = useState<string | null>(null);
  const selectorId = useId();
  const orderSelectorId = useId();
  const reports = snapshot?.reports || [];
  const report = reports.find((item) => item.id === selectedId) || reports.find((item) => item.has_xml) || reports[0];
  const check = report?.checks.find((item) => item.rule_code === '1004d');
  const order =
    snapshot?.orders.find((item) => item.id === selectedOrderId) ||
    snapshot?.orders.find((item) => item.id === report?.order_id) ||
    snapshot?.orders[0];
  const facts = report?.facts;
  const feeRows = snapshot?.feeCompliance || [];
  const feeStatus = feeAuditStatus(feeRows);
  const canAct = Boolean(snapshot?.eligible && snapshot.mode !== 'off' && !busy && !loading);
  // Payment links have their own flag: present only while it is on, and never for Transferred Data.
  const paymentLinks =
    onPreviewReminder && onSendReminder && onAddPaymentLink && onListUnresolvedPaymentLinks && onResolveReminder
      ? snapshot?.paymentLinks || null
      : null;
  const canPay = Boolean(paymentLinks && snapshot?.eligible && !busy && !loading);
  const canSendReminder = Boolean(
    canPay && paymentLinks?.reminderEnabled && paymentLinks.requests.some((request) => !request.reminderOpen)
  );
  const events = useMemo(
    () =>
      (snapshot?.events || []).filter(
        (event) => filter === 'all' || (filter === 'message' ? event.kind === 'message' : event.kind !== 'message')
      ),
    [snapshot?.events, filter]
  );
  const documents = useMemo(() => {
    const seen = new Set<string>();
    return (snapshot?.events || []).flatMap((event) =>
      event.resources.flatMap((resource) => {
        const key = `${event.order_id}:${resource.id}`;
        if (seen.has(key)) {
          return [];
        }
        seen.add(key);
        return [{ ...resource, orderId: event.order_id }];
      })
    );
  }, [snapshot?.events]);
  const stateTitle =
    check?.headline ||
    (report?.status === 'handled_manually'
      ? 'XML unobtainable · handled manually'
      : report?.status === 'failed'
        ? 'Appraisal processing failed'
        : report?.status === 'needs_manual_review'
          ? 'Appraisal needs review'
          : report?.has_xml
            ? 'Ready for the 1004D audit'
            : report
              ? 'Waiting for appraisal XML'
              : 'Your appraisal workspace');
  const stateToneValue: LwStatusTone =
    check?.outcome === 'passed'
      ? 'success'
      : check?.outcome === 'required' || check?.outcome === 'review' || report?.status === 'needs_manual_review'
        ? 'warning'
        : report?.status === 'failed'
          ? 'destructive'
          : 'neutral';
  const ResultIcon =
    check?.outcome === 'passed'
      ? CheckCheck
      : stateToneValue === 'warning' || stateToneValue === 'destructive'
        ? TriangleAlert
        : ClipboardCheck;
  const resultCopy =
    check?.reason ||
    report?.last_error ||
    (report?.status === 'pending_extraction'
      ? 'The uploaded document is being read before it can be identified as a full appraisal.'
      : report
        ? 'We need the original XML to read the selected appraisal conditions accurately.'
        : 'Order dates, AMC messages, report facts, and the 1004D audit come together here.');
  const disabledReason = !snapshot?.eligible
    ? snapshot?.eligibilityReason
    : snapshot?.mode === 'off'
      ? 'The Appraisal workspace is awaiting rollout.'
      : null;

  return (
    <LwThemeScope className="appraisal-workspace">
      <div data-testid="appraisal-workspace">
        <header className="appraisal-header">
          <div>
            <div className="appraisal-eyebrow">PROPERTY & VALUATION</div>
            <h2>Appraisal</h2>
            <p>One place for the report, the conversation, and what comes next.</p>
          </div>
          <div className="appraisal-header__actions">
            <LwButton variant="sm" disabled={!canAct} onClick={onRefresh}>
              <RefreshCw size={14} className={snapshot?.refreshPending ? 'animate-spin' : ''} aria-hidden="true" />
              Refresh
            </LwButton>
            {paymentLinks && (
              <AppraisalActionsMenu
                disabled={!canPay}
                canSend={canSendReminder}
                onSend={(trigger) => setSendFrom(trigger)}
                onAdd={(trigger) => setAddFrom(trigger)}
              />
            )}
            <LwButton variant="sm-solid" disabled={!canAct} onClick={() => setUploadOpen(true)}>
              <ArrowUpFromLine size={14} aria-hidden="true" />
              Upload XML
            </LwButton>
          </div>
        </header>
        {error && (
          <div role="alert" className="appraisal-notice appraisal-notice--error">
            <TriangleAlert size={18} aria-hidden="true" />
            <div>
              <strong>Appraisal information could not be refreshed.</strong>
              <p>
                {error}
                {snapshot && ' Saved information is shown below.'}
              </p>
              <LwButton variant="sm" onClick={onRefresh} disabled={Boolean(busy)}>
                Try again
              </LwButton>
            </div>
          </div>
        )}
        {disabledReason && (
          <div className="appraisal-notice">
            <Info size={17} aria-hidden="true" />
            <p>{disabledReason}</p>
          </div>
        )}
        {snapshot?.mode === 'assessment' && (
          <div className="appraisal-mode-note">
            <span className="appraisal-mode-dot" />
            Assessment mode <span>Audits record findings. Conditions and audit emails are held.</span>
          </div>
        )}
        {loading && !snapshot ? (
          <div className="appraisal-empty" role="status">
            <Loader2 className="animate-spin" aria-hidden="true" />
            <h3>Loading appraisal information…</h3>
          </div>
        ) : (
          <>
            {reports.length > 0 && (
              <div className="appraisal-report-picker">
                <div>
                  <label htmlFor={selectorId}>REPORT VERSION</label>
                  <select
                    id={selectorId}
                    value={report?.id || ''}
                    onChange={(event) => {
                      setSelectedId(event.target.value);
                      setSelectedOrderId('');
                    }}
                  >
                    {reports.map((item, index) => (
                      <option key={item.id} value={item.id}>
                        {item.source_name} · {appraisalDate(item.received_at || item.created_at)}
                        {index === 0 ? ' · latest received' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <span>
                  {report?.source === 'mercury'
                    ? 'Mercury'
                    : report?.source === 'upload'
                      ? 'Uploaded XML'
                      : 'eFolder upload'}
                  {report?.findings_only && ' · findings only'}
                </span>
              </div>
            )}
            <section
              className={`appraisal-audit appraisal-audit--${stateToneValue}`}
              aria-labelledby="appraisal-audit-title"
            >
              <div className="appraisal-audit__heading">
                <span className="appraisal-audit__icon">
                  <ResultIcon size={25} aria-hidden="true" />
                </span>
                <div>
                  <div className="appraisal-eyebrow">1004D AUDIT</div>
                  <h3 id="appraisal-audit-title">{stateTitle}</h3>
                  <p>{resultCopy}</p>
                </div>
                <LwButton
                  variant="sm-solid"
                  disabled={!canAct || !report?.has_xml}
                  onClick={() => {
                    if (report) {
                      onAudit(report.id);
                    }
                  }}
                >
                  <ClipboardCheck size={15} aria-hidden="true" />
                  {busy === 'audit' ? 'Auditing…' : 'Run audit'}
                </LwButton>
              </div>
              {check?.requirements && check.outcome !== 'passed' && (
                <details open className="appraisal-requirements">
                  <summary>Requirements from the appraisal</summary>
                  <p>{check.requirements}</p>
                </details>
              )}
              {report?.last_error && check && <p className="appraisal-error">{report.last_error}</p>}
              {report && (
                <div className="appraisal-outcomes">
                  <div>
                    <span className="appraisal-outcomes__label">
                      Enhanced condition{' '}
                      {report.condition_code && (
                        <strong>
                          {report.condition_code}
                          {report.condition_prior_to ? ` · Prior to ${report.condition_prior_to}` : ''}
                        </strong>
                      )}
                    </span>
                    <LwStatusPill
                      tone={stateTone(report.condition_state)}
                      label={STATE_LABELS[report.condition_state] || report.condition_state}
                    />
                  </div>
                  <div>
                    <span className="appraisal-outcomes__label">
                      Team notification{' '}
                      {report.email_sent_at && (
                        <small>
                          {appraisalDate(report.email_sent_at)} ·{' '}
                          {report.delivery_method === 'attachment' ? 'PDF attached' : 'Loan Center link'}
                        </small>
                      )}
                    </span>
                    <LwStatusPill
                      tone={stateTone(report.email_state)}
                      label={STATE_LABELS[report.email_state] || report.email_state}
                    />
                  </div>
                </div>
              )}
              {report?.resolution_note && (
                <p className="appraisal-resolution">
                  <strong>Handled manually · {appraisalDate(report.resolved_at)}</strong>
                  <br />
                  {report.resolution_note}
                </p>
              )}
              {report && !report.has_xml && snapshot?.canResolve && report.status === 'needs_manual_review' && (
                <div className="appraisal-audit__footer">
                  <LwButton variant="sm" disabled={!canAct} onClick={() => setResolveOpen(true)}>
                    XML unobtainable — handled manually
                  </LwButton>
                </div>
              )}
              {report?.findings_only && (
                <p className="appraisal-audit__note">
                  This report is findings only. Running the audit again will not add a condition or send an email.
                </p>
              )}
            </section>

            <section className={`appraisal-audit appraisal-audit--${feeStatus.tone}`} aria-labelledby="appraisal-fee-audit-title">
              <div className="appraisal-audit__heading">
                <span className="appraisal-audit__icon"><ClipboardCheck size={25} aria-hidden="true" /></span>
                <div>
                  <div className="appraisal-eyebrow">FEE COMPLIANCE AUDIT</div>
                  <h3 id="appraisal-fee-audit-title">{feeStatus.title}</h3>
                  <p>{feeStatus.description}</p>
                </div>
              </div>
            </section>

            <section className="appraisal-dates" aria-label="Appraisal key dates">
              <div className="appraisal-section-heading">
                <h3>
                  <CalendarDays size={16} aria-hidden="true" />
                  Key dates
                </h3>
                <span>{orderLabel(order)}</span>
              </div>
              {(snapshot?.orders.length || 0) > 1 && (
                <div className="appraisal-report-picker">
                  <div>
                    <label htmlFor={orderSelectorId}>APPRAISAL ORDER</label>
                    <select
                      id={orderSelectorId}
                      value={order?.id || ''}
                      onChange={(event) => setSelectedOrderId(event.target.value)}
                    >
                      {snapshot?.orders.map((item) => (
                        <option key={item.id} value={item.id}>
                          {orderLabel(item)} · {appraisalDate(item.ordered_at)}
                          {item.status ? ` · ${item.status}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              <dl>
                {[
                  ['Ordered', order?.ordered_at ? appraisalDate(order.ordered_at) : 'Not received'],
                  ['Accepted', order?.accepted_at ? appraisalDate(order.accepted_at) : 'Not received'],
                  [
                    'Inspection',
                    order?.inspection_display ||
                      (order?.inspection_at ? appraisalDate(order.inspection_at, true) : 'Not scheduled')
                  ],
                  [
                    'Requested due',
                    order?.requested_due_date ? appraisalDate(order.requested_due_date) : 'Not provided'
                  ],
                  [
                    'First received',
                    order?.first_received_at ? appraisalDate(order.first_received_at) : 'Not received'
                  ],
                  [
                    'Latest delivery',
                    order?.latest_received_at ? appraisalDate(order.latest_received_at) : 'Not received'
                  ]
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd className={value.startsWith('Not ') ? 'is-empty' : ''}>{value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <div className="appraisal-columns">
              <LwDataPanel
                title="Order activity"
                description="AMC messages and meaningful order updates."
                className="appraisal-activity"
                actions={
                  <div className="appraisal-filters" role="group" aria-label="Filter appraisal activity">
                    {(['all', 'message', 'update'] as const).map((value) => (
                      <button
                        type="button"
                        key={value}
                        aria-pressed={filter === value}
                        onClick={() => setFilter(value)}
                      >
                        {value === 'all' ? 'All' : value === 'message' ? 'Messages' : 'Updates'}
                      </button>
                    ))}
                  </div>
                }
                footer={
                  <span className="appraisal-freshness">
                    {snapshot?.refreshPending
                      ? 'Checking for updates…'
                      : snapshot?.refreshedAt
                        ? `Updated ${appraisalDate(snapshot.refreshedAt, true)}`
                        : 'Updates appear when an appraisal order is found.'}
                  </span>
                }
              >
                {events.length ? (
                  <ol className="appraisal-timeline">
                    {events.map((event) => (
                      <TimelineEntry
                        key={event.id}
                        event={event}
                        order={snapshot?.orders.find((item) => item.id === event.order_id)}
                        onDownload={onResourceDownload}
                      />
                    ))}
                  </ol>
                ) : (
                  <div className="appraisal-empty">
                    <MessageSquare size={26} aria-hidden="true" />
                    <h4>{filter === 'all' ? 'No order activity yet' : 'No matching activity'}</h4>
                    <p>
                      {filter === 'all'
                        ? 'Refresh to check for an order. Delivered XML can also be uploaded above.'
                        : 'Choose another filter to see the remaining updates.'}
                    </p>
                  </div>
                )}
              </LwDataPanel>
              <aside className="appraisal-sidebar" aria-label="Appraisal facts and documents">
                {paymentLinks && (
                  <PaymentLinkPanel
                    links={paymentLinks}
                    canResolve={Boolean(snapshot?.canResolve)}
                    disabled={!canPay}
                    formatDate={appraisalDate}
                    onResolve={setResolveAttempt}
                  />
                )}
                <LwDataPanel title="Report facts" actions={<House size={17} aria-hidden="true" />}>
                  {facts ? (
                    <>
                      <div className="appraisal-value">
                        <span>APPRAISED VALUE</span>
                        <strong>
                          {facts.value
                            ? new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                maximumFractionDigits: 0
                              }).format(facts.value)
                            : 'Not provided'}
                        </strong>
                      </div>
                      <p className="appraisal-property">
                        {[facts.property.street, facts.property.unit && `Unit ${facts.property.unit}`]
                          .filter(Boolean)
                          .join(', ')}
                        <br />
                        {[facts.property.city, facts.property.state, facts.property.postalCode]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                      <dl className="appraisal-facts">
                        {[
                          ['Location', facts.location || 'Not provided'],
                          ['Effective date', facts.effectiveDate ? appraisalDate(facts.effectiveDate) : 'Not provided'],
                          ['Form', facts.form || 'Not provided'],
                          ['Appraiser', facts.appraiser.name || 'Not provided'],
                          ['Company', facts.appraiser.company || 'Not provided'],
                          [
                            'License',
                            [facts.appraiser.licenseState, facts.appraiser.license].filter(Boolean).join(' · ') ||
                              'Not provided'
                          ],
                          [
                            'License expires',
                            facts.appraiser.licenseExpires
                              ? appraisalDate(facts.appraiser.licenseExpires)
                              : 'Not provided'
                          ],
                          ['Report identifier', facts.fileNumber || 'Not provided']
                        ].map(([label, value]) => (
                          <div key={label}>
                            <dt>{label}</dt>
                            <dd>{label === 'Location' ? <LwStatusPill tone="neutral" label={value} /> : value}</dd>
                          </div>
                        ))}
                      </dl>
                    </>
                  ) : (
                    <div className="appraisal-empty appraisal-empty--small">
                      <FileCode2 size={25} aria-hidden="true" />
                      <p>Property, appraiser, value, and location facts appear after XML ingestion.</p>
                    </div>
                  )}
                </LwDataPanel>
                <AppraisalFeeComplianceCard rows={feeRows} orderId={report?.order_id} selectedFindingId={initialFeeVerificationId} />
                <LwDataPanel title="Documents" description="Original files for this loan.">
                  <div className="appraisal-documents">
                    {report?.has_pdf && (
                      <button type="button" onClick={() => onDownload(report.id, 'pdf')}>
                        <FileText size={18} aria-hidden="true" />
                        <span>
                          <strong>Appraisal report</strong>
                          <small>Verified PDF for this version</small>
                        </span>
                        <ArrowDownToLine size={15} aria-hidden="true" />
                      </button>
                    )}
                    {report?.has_xml && (
                      <button type="button" onClick={() => onDownload(report.id, 'xml')}>
                        <FileCode2 size={18} aria-hidden="true" />
                        <span>
                          <strong>Original XML</strong>
                          <small>Source for the report facts</small>
                        </span>
                        <ArrowDownToLine size={15} aria-hidden="true" />
                      </button>
                    )}
                    {documents.slice(0, 6).map((document) => (
                      <button
                        type="button"
                        key={`${document.orderId}:${document.id}`}
                        onClick={() => onResourceDownload(document.orderId, document.id, document.name)}
                      >
                        <FileText size={18} aria-hidden="true" />
                        <span>
                          <strong>{document.name}</strong>
                          <small>From provider</small>
                        </span>
                        <ArrowDownToLine size={15} aria-hidden="true" />
                      </button>
                    ))}
                  </div>
                  {!report?.has_pdf && !report?.has_xml && !documents.length && (
                    <p className="appraisal-help">Delivered documents will appear here.</p>
                  )}
                  {documents.length > 6 && (
                    <p className="appraisal-help">
                      All {documents.length} provider documents are available in the activity feed.
                    </p>
                  )}
                </LwDataPanel>
              </aside>
            </div>
          </>
        )}
        {uploadOpen && <XmlUploadModal report={report} onClose={() => setUploadOpen(false)} onUpload={onUpload} />}
        {resolveOpen && report && (
          <ResolveModal report={report} onClose={() => setResolveOpen(false)} onResolve={onResolve} />
        )}
        {paymentLinks && sendFrom !== false && onPreviewReminder && onSendReminder && (
          <SendPaymentReminderDialog
            requests={paymentLinks.requests}
            returnFocusTo={sendFrom}
            formatDate={appraisalDate}
            onClose={() => setSendFrom(false)}
            onPreview={onPreviewReminder}
            onSend={onSendReminder}
          />
        )}
        {paymentLinks && addFrom !== false && onAddPaymentLink && onListUnresolvedPaymentLinks && (
          <AddPaymentLinkModal
            current={paymentLinks.requests[0]}
            isAdmin={Boolean(snapshot?.canResolve)}
            formatDate={appraisalDate}
            returnFocusTo={addFrom}
            onClose={() => setAddFrom(false)}
            onAdd={onAddPaymentLink}
            onListUnresolved={onListUnresolvedPaymentLinks}
          />
        )}
        {resolveAttempt && onResolveReminder && (
          <ResolveReminderModal attemptId={resolveAttempt} onClose={() => setResolveAttempt(null)} onResolve={onResolveReminder} />
        )}
      </div>
    </LwThemeScope>
  );
}
